import { parseCSV } from './csv'
import { validateRecords, sanitizeRecords } from './validation'

/**
 * Process CSV import file with validation
 * @param {File} file - CSV file to import
 * @param {Array<string>} existingDIDs - Array of existing DIDs in the database
 * @returns {Promise<Object>} - Result object with success status, records, and errors
 */
export const processCSVImport = async (file, existingDIDs = []) => {
  const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB in bytes

  // Validate file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      success: false,
      errors: [{
        row: 0,
        column: 'File',
        message: `File size exceeds 10MB limit (${(file.size / 1024 / 1024).toFixed(2)}MB)`,
        value: file.name
      }]
    }
  }

  try {
    // Parse CSV file
    const parsedData = await parseCSV(file)

    if (!parsedData || parsedData.length === 0) {
      return {
        success: false,
        errors: [{
          row: 0,
          column: 'File',
          message: 'CSV file is empty or contains no valid data',
          value: file.name
        }]
      }
    }

    // Validate required columns are present
    const requiredColumns = ['DID', 'Title', 'Location/URL', 'Owner', 'Status']
    const firstRecord = parsedData[0]
    const fileColumns = Object.keys(firstRecord)
    
    // Normalize column names for comparison (case-insensitive, trim whitespace)
    const normalizedFileColumns = fileColumns.map(col => col.toLowerCase().trim())
    const missingColumns = []

    requiredColumns.forEach(reqCol => {
      const normalizedReqCol = reqCol.toLowerCase().trim()
      if (!normalizedFileColumns.includes(normalizedReqCol)) {
        missingColumns.push(reqCol)
      }
    })

    if (missingColumns.length > 0) {
      return {
        success: false,
        errors: [{
          row: 0,
          column: 'File',
          message: `Missing required columns: ${missingColumns.join(', ')}`,
          value: file.name
        }]
      }
    }

    // Normalize records to use standard field names (lowercase)
    const normalizedRecords = parsedData.map(record => {
      const normalized = {}
      Object.keys(record).forEach(key => {
        const normalizedKey = key.toLowerCase().trim()
        if (normalizedKey === 'did') {
          normalized.did = record[key]
        } else if (normalizedKey === 'title') {
          normalized.title = record[key]
        } else if (normalizedKey === 'location/url') {
          normalized.location = record[key]
          // Auto-generate filename from URL (extract last path segment or use title)
          try {
            const url = new URL(record[key])
            const pathSegments = url.pathname.split('/').filter(s => s)
            normalized.filename = pathSegments.length > 0 
              ? pathSegments[pathSegments.length - 1] 
              : record[key]
          } catch {
            // If URL parsing fails, use the location as filename
            normalized.filename = record[key]
          }
        } else if (normalizedKey === 'owner') {
          normalized.owner = record[key]
        } else if (normalizedKey === 'status') {
          normalized.status = record[key]
        }
      })
      return normalized
    })

    // Validate records
    const validationErrors = validateRecords(normalizedRecords, existingDIDs)

    if (validationErrors.length > 0) {
      return {
        success: false,
        errors: validationErrors,
        recordCount: normalizedRecords.length
      }
    }

    // Sanitize records for XSS prevention
    const sanitizedRecords = sanitizeRecords(normalizedRecords)

    return {
      success: true,
      records: sanitizedRecords,
      recordCount: sanitizedRecords.length
    }
  } catch (error) {
    return {
      success: false,
      errors: [{
        row: 0,
        column: 'File',
        message: `Failed to parse CSV file: ${error.message}`,
        value: file.name
      }]
    }
  }
}
